import { z } from 'zod';
import type { AddFields } from '../add-fields.ts';

// Browser-safe: the contract imports this, so no node: or database imports.

export type Side = 'long' | 'short';

/** What the post asks of a trade in the analyst's book, by the words that name it. */
interface FollowUp {
  /** The post's own words for the trade, which must single out `trades`. */
  named: string;
  trades: number[];
  side: Side | null;
}

/**
 * One thing a post asks, as the reader read it. `said` is the post's own words for it, and
 * `stated` every number the reading takes from them: each must be in `said`.
 */
export type Intent = { said: string; stated: number[] } & (
  | {
      kind: 'open';
      fields: AddFields;
      band?: [number, number];
      /** Every target the post gives, the one filed among them. */
      targets?: number[];
      note: string | null;
    }
  | ({ kind: 'close'; price: number | null; reported?: boolean } & FollowUp)
  | ({ kind: 'move'; target: number | null; stoploss: number | null } & FollowUp)
  | ({ kind: 'split'; targets: number[] } & FollowUp)
  | { kind: 'unclear'; might: 'open' | 'close' | 'move' | 'split' }
);

/**
 * `done` changed the book; `held` waits on Drafts; `asks` needs a person and has nothing to
 * approve; `passed` needed nothing (already so, or nothing open by that name).
 */
export const ACT_STATUSES = ['done', 'held', 'asks', 'passed'] as const;

export type ActStatus = (typeof ACT_STATUSES)[number];

export interface Outcome {
  status: ActStatus;
  line: string;
  why: string[];
  recommendationIds: number[];
  link?: { action: string; href: string };
}

export type Speaks = 'now' | 'later' | 'not';

/**
 * A second read of the post, of one act: whether the post asks for it now, plans it for later or
 * only speaks of it (null where unsure), and what it doubts, each in a sentence.
 */
export interface Review {
  speaks: Speaks | null;
  doubts: string[];
  answers?: Record<string, { choice: string; confidence: number }>;
}

/** A post's reading: what it asks, each with its review and what became of it once decided. */
export interface PostReading {
  /** `review` is null where no second read answered, so nothing of the act is done unasked. */
  acts: { intent: Intent; review?: Review | null; outcome: Outcome | null }[];
  raw?: unknown;
  /** An edit of a post that had acted: what it changed, null where nothing that matters did. */
  edited?: Outcome | null;
  repeatOf?: string;
  told?: boolean;
}

export const readingTallySchema = z.object({
  /** Only posts that have a reading, not the whole archive. */
  posts: z.coerce.number().int(),
  quiet: z.coerce.number().int(),
  done: z.coerce.number().int(),
  held: z.coerce.number().int(),
  asks: z.coerce.number().int(),
});

export type ReadingTally = z.infer<typeof readingTallySchema>;
